import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Chip,
  Divider,
  Avatar,
  AppBar,
  Toolbar,
  Stack,
  CardMedia,
  TextField,
  InputAdornment,
  Breadcrumbs,
  Link as MuiLink,
} from '@mui/material';
import {
  GraduationCap,
  Megaphone,
  Calendar,
  Eye,
  Search,
  ArrowLeft,
  Share2,
  LogIn,
  LayoutDashboard,
  Home,
  ChevronRight,
  Pin,
  Paperclip,
  FileText,
  Download,
} from 'lucide-react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import api from '../services/api';
import { GlassCard } from '../components/GlassCard';
import { Footer } from '../components/Footer';

const resolveFileUrl = (url?: string) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/uploads/')) return `/api/v1${url}`;
  if (!url.startsWith('/')) return `/api/v1/uploads/${url}`;
  return url;
};

const PublicAnnouncements: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isLoggedIn = Boolean(token && user?.id);

  const [announcements, setAnnouncements] = useState<any[]>([]);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<any | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  // Fetch all public announcements
  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const res = await api.get('/announcements/public?limit=50');
      setAnnouncements(res.data || []);
    } catch (err) {
      console.error('Failed to load announcements', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch single announcement when id param exists
  const fetchSingleAnnouncement = async (annId: string) => {
    try {
      setLoading(true);
      const res = await api.get(`/announcements/public/${annId}`);
      setSelectedAnnouncement(res.data);
      document.title = `${res.data.title} | ข่าวประชาสัมพันธ์ TPMS KPRU`;
    } catch (err) {
      console.error('Failed to load announcement detail', err);
      setSelectedAnnouncement(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  useEffect(() => {
    if (id) {
      fetchSingleAnnouncement(id);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setSelectedAnnouncement(null);
      document.title = 'ข่าวประชาสัมพันธ์และประกาศทั้งหมด | TPMS KPRU';
    }
  }, [id]);

  const filteredAnnouncements = announcements.filter((a) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      a.title?.toLowerCase().includes(term) ||
      a.content?.toLowerCase().includes(term) ||
      a.author_name?.toLowerCase().includes(term)
    );
  });

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: selectedAnnouncement?.title,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('คัดลอกลิงก์ข่าวประชาสัมพันธ์เรียบร้อยแล้ว');
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC', display: 'flex', flexDirection: 'column' }}>
      {/* Sticky App Header */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          background: 'rgba(255, 255, 255, 0.92)',
          backdropFilter: 'blur(16px)',
          borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)',
          color: '#0F172A',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ justifyContent: 'space-between', minHeight: 70 }}>
            {/* Logo */}
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }}
              onClick={() => navigate('/')}
            >
              <Avatar
                src="/logo.png"
                alt="TPMS KPRU"
                sx={{
                  bgcolor: 'primary.main',
                  width: 42,
                  height: 42,
                  p: 0.3,
                  boxShadow: '0 4px 10px rgba(30, 64, 175, 0.22)',
                }}
              >
                <GraduationCap size={22} color="#fff" />
              </Avatar>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: -0.5, color: '#1E3A8A' }}>
                    TPMS
                  </Typography>
                  <Chip
                    label="KPRU"
                    size="small"
                    sx={{
                      bgcolor: 'rgba(217, 119, 6, 0.12)',
                      color: '#B45309',
                      fontWeight: 700,
                      height: 20,
                      fontSize: '0.7rem',
                    }}
                  />
                </Box>
                <Typography variant="caption" sx={{ color: '#64748B', display: { xs: 'none', sm: 'block' } }}>
                  ข่าวประชาสัมพันธ์และประกาศทางการ
                </Typography>
              </Box>
            </Box>

            {/* Header Right Actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Button
                color="inherit"
                startIcon={<Home size={18} />}
                onClick={() => navigate('/')}
                sx={{ fontWeight: 600, color: '#475569' }}
              >
                หน้าแรก
              </Button>
              <Button
                variant={isLoggedIn ? 'contained' : 'outlined'}
                color="primary"
                startIcon={isLoggedIn ? <LayoutDashboard size={18} /> : <LogIn size={18} />}
                onClick={() => navigate(isLoggedIn ? '/dashboard' : '/login')}
                sx={{ fontWeight: 600, borderRadius: 2.5, px: 2.2 }}
              >
                {isLoggedIn ? 'เข้าสู่ระบบปฏิบัติงาน' : 'เข้าสู่ระบบ'}
              </Button>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Main Body */}
      <Container maxWidth="xl" sx={{ py: 4, flexGrow: 1 }}>
        {/* Breadcrumb Navigation */}
        <Breadcrumbs
          separator={<ChevronRight size={14} />}
          sx={{ mb: 3, '& .MuiBreadcrumbs-li': { fontSize: '0.88rem' } }}
        >
          <MuiLink
            component={Link}
            to="/"
            underline="hover"
            color="inherit"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <Home size={14} /> หน้าแรก
          </MuiLink>
          <MuiLink
            component={Link}
            to="/announcements"
            underline="hover"
            color={selectedAnnouncement ? 'inherit' : 'primary.main'}
            sx={{ fontWeight: selectedAnnouncement ? 500 : 700 }}
          >
            ข่าวประชาสัมพันธ์และประกาศ
          </MuiLink>
          {selectedAnnouncement && (
            <Typography color="text.primary" noWrap sx={{ fontWeight: 700, maxWidth: { xs: 200, sm: 400 } }}>
              {selectedAnnouncement.title}
            </Typography>
          )}
        </Breadcrumbs>

        {/* VIEW 1: SINGLE ANNOUNCEMENT FULL ARTICLE VIEW */}
        {selectedAnnouncement ? (
          <Grid container spacing={4}>
            {/* Main Article Column */}
            <Grid size={{ xs: 12, md: 8 }}>
              <GlassCard highlightColor="#2563EB" sx={{ p: { xs: 2.5, sm: 4 }, overflow: 'hidden' }}>
                {/* Back button */}
                <Button
                  startIcon={<ArrowLeft size={16} />}
                  onClick={() => navigate('/announcements')}
                  sx={{ mb: 2.5, color: '#64748B' }}
                >
                  กลับไปหน้ารวมข่าวสาร
                </Button>

                {/* Banner Image */}
                {selectedAnnouncement.banner_url && (
                  <Box
                    sx={{
                      width: '100%',
                      maxHeight: { xs: 240, sm: 400 },
                      borderRadius: 2.5,
                      overflow: 'hidden',
                      mb: 3,
                      bgcolor: '#F1F5F9',
                      border: '1px solid #E2E8F0',
                    }}
                  >
                    <CardMedia
                      component="img"
                      image={resolveFileUrl(selectedAnnouncement.banner_url)}
                      alt={selectedAnnouncement.title}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </Box>
                )}

                {/* Meta details */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
                  {selectedAnnouncement.is_pinned ? (
                    <Chip
                      icon={<Pin size={13} />}
                      label="ข่าวประชาสัมพันธ์สำคัญ"
                      size="small"
                      sx={{ bgcolor: '#FEF3C7', color: '#B45309', fontWeight: 700 }}
                    />
                  ) : null}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#64748B', fontSize: '0.85rem' }}>
                    <Calendar size={15} />
                    <span>
                      {new Date(selectedAnnouncement.created_at).toLocaleDateString('th-TH', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                      })}
                    </span>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#64748B', fontSize: '0.85rem' }}>
                    <Eye size={15} />
                    <span>ผู้เข้าชม {selectedAnnouncement.views_count || 1} ครั้ง</span>
                  </Box>
                </Box>

                {/* Title */}
                <Typography
                  variant="h4"
                  component="h1"
                  sx={{
                    fontWeight: 800,
                    color: '#1E3A8A',
                    lineHeight: 1.35,
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                    mb: 3,
                  }}
                >
                  {selectedAnnouncement.title}
                </Typography>

                <Divider sx={{ mb: 3.5 }} />

                {/* Rich HTML Content Body */}
                <Box
                  sx={{
                    fontFamily: "'Sarabun', sans-serif",
                    fontSize: { xs: '1rem', sm: '1.05rem' },
                    lineHeight: 1.85,
                    color: '#1E293B',
                    '& h2': {
                      color: '#1E3A8A',
                      fontFamily: "'Prompt', sans-serif",
                      fontWeight: 700,
                      mt: 3,
                      mb: 1.5,
                      fontSize: '1.45rem',
                    },
                    '& h3': {
                      color: '#1E40AF',
                      fontFamily: "'Prompt', sans-serif",
                      fontWeight: 600,
                      mt: 2.5,
                      mb: 1.2,
                      fontSize: '1.25rem',
                    },
                    '& p': { mb: 1.5 },
                    '& ul, & ol': { pl: 3, mb: 1.5 },
                    '& li': { mb: 0.5 },
                    '& blockquote': {
                      borderLeft: '4px solid #3B82F6',
                      bgcolor: '#F8FAFC',
                      p: 2,
                      my: 2,
                      borderRadius: '0 8px 8px 0',
                      fontStyle: 'italic',
                      color: '#475569',
                    },
                    '& img': { maxWidth: '100%', height: 'auto', borderRadius: 8, my: 2 },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: (selectedAnnouncement.content || '')
                      .replace(/src="\/uploads\//g, 'src="/api/v1/uploads/')
                      .replace(/href="\/uploads\//g, 'href="/api/v1/uploads/'),
                  }}
                />

                {/* Related Attachments Section */}
                {(() => {
                  let atts: any[] = [];
                  try {
                    if (selectedAnnouncement.attachments_json) {
                      atts = typeof selectedAnnouncement.attachments_json === 'string'
                        ? JSON.parse(selectedAnnouncement.attachments_json)
                        : selectedAnnouncement.attachments_json;
                    }
                  } catch (e) {
                    atts = [];
                  }

                  if (!Array.isArray(atts) || atts.length === 0) return null;

                  return (
                    <Box sx={{ mt: 4, mb: 2, p: 2.5, bgcolor: '#F8FAFC', borderRadius: 2.5, border: '1px solid #E2E8F0' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <Paperclip size={18} color="#1E40AF" />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E3A8A' }}>
                          เอกสารแนบที่เกี่ยวข้อง ({atts.length} ไฟล์)
                        </Typography>
                      </Box>
                      <Stack spacing={1}>
                        {atts.map((att: any, idx: number) => (
                          <Box
                            key={idx}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              bgcolor: '#FFFFFF',
                              p: 1.5,
                              px: 2,
                              borderRadius: 2,
                              border: '1px solid #E2E8F0',
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, overflow: 'hidden' }}>
                              <FileText size={20} color="#059669" />
                              <Box sx={{ overflow: 'hidden' }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B' }} noWrap>
                                  {att.name}
                                </Typography>
                                {att.size && (
                                  <Typography variant="caption" color="text.secondary">
                                    {att.size > 1024 * 1024
                                      ? `${(att.size / (1024 * 1024)).toFixed(1)} MB`
                                      : `${Math.round(att.size / 1024)} KB`}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                            <Button
                              size="small"
                              variant="outlined"
                              href={resolveFileUrl(att.url)}
                              target="_blank"
                              rel="noopener noreferrer"
                              startIcon={<Download size={14} />}
                              sx={{ borderRadius: 1.5, flexShrink: 0, ml: 2, fontWeight: 600 }}
                            >
                              ดาวน์โหลด
                            </Button>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  );
                })()}

                <Divider sx={{ my: 4 }} />

                {/* Footer of Article */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      ฝ่ายฝึกประสบการณ์วิชาชีพครู คณะครุศาสตร์ มหาวิทยาลัยราชภัฏกำแพงเพชร
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ผู้ลงประกาศ: <strong>{selectedAnnouncement.author_name}</strong>
                    </Typography>
                  </Box>

                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Share2 size={16} />}
                    onClick={handleShare}
                    sx={{ borderRadius: 2 }}
                  >
                    แชร์ข่าวสารนี้
                  </Button>
                </Box>
              </GlassCard>
            </Grid>

            {/* Sidebar with Latest Other Announcements */}
            <Grid size={{ xs: 12, md: 4 }}>
              <GlassCard sx={{ p: 3, position: 'sticky', top: 90 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Megaphone size={20} color="#1E40AF" />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E3A8A' }}>
                    ข่าวประชาสัมพันธ์อื่นๆ
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />

                <Stack spacing={2}>
                  {announcements
                    .filter((a) => a.id !== selectedAnnouncement.id)
                    .slice(0, 6)
                    .map((other) => (
                      <Box
                        key={other.id}
                        sx={{
                          cursor: 'pointer',
                          p: 1.5,
                          borderRadius: 2,
                          transition: 'background-color 0.15s ease',
                          '&:hover': { bgcolor: '#F1F5F9' },
                        }}
                        onClick={() => navigate(`/announcements/${other.id}`)}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 700,
                            color: '#1E3A8A',
                            lineHeight: 1.4,
                            mb: 0.5,
                            '&:hover': { color: '#2563EB' },
                          }}
                        >
                          {other.title}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#64748B', fontSize: '0.78rem' }}>
                          <Calendar size={13} />
                          <span>
                            {new Date(other.created_at).toLocaleDateString('th-TH', {
                              day: 'numeric',
                              month: 'short',
                            })}
                          </span>
                        </Box>
                      </Box>
                    ))}
                </Stack>
              </GlassCard>
            </Grid>
          </Grid>
        ) : (
          /* VIEW 2: ALL ANNOUNCEMENTS GRID VIEW */
          <Box>
            {/* Page Title & Search Bar */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', md: 'center' },
                gap: 2.5,
                mb: 4,
              }}
            >
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Megaphone size={30} color="#1E40AF" />
                  <Typography variant="h4" sx={{ fontWeight: 800, color: '#0F172A' }}>
                    ข่าวประชาสัมพันธ์และประกาศทั้งหมด
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  ศูนย์รวมข่าวสาร กำหนดการ และประกาศจากฝ่ายฝึกประสบการณ์วิชาชีพครู คณะครุศาสตร์ มรภ.กำแพงเพชร
                </Typography>
              </Box>

              <TextField
                placeholder="ค้นหาตามหัวข้อหรือเนื้อหาข่าว..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                sx={{
                  width: { xs: '100%', sm: 320 },
                  bgcolor: '#FFFFFF',
                  borderRadius: 2,
                }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search size={18} color="#64748B" />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Box>

            {/* Grid of Announcements */}
            <Grid container spacing={3}>
              {filteredAnnouncements.map((ann) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={ann.id}>
                  <GlassCard
                    highlightColor={ann.is_pinned ? '#D97706' : '#2563EB'}
                    onClick={() => navigate(`/announcements/${ann.id}`)}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 12px 28px rgba(0, 0, 0, 0.08)',
                      },
                    }}
                  >
                    {/* Banner Thumbnail */}
                    <Box
                      sx={{
                        width: '100%',
                        height: 180,
                        bgcolor: '#EFF6FF',
                        overflow: 'hidden',
                        position: 'relative',
                      }}
                    >
                      {ann.banner_url ? (
                        <CardMedia
                          component="img"
                          image={resolveFileUrl(ann.banner_url)}
                          alt={ann.title}
                          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%)',
                            color: '#FFFFFF',
                          }}
                        >
                          <Megaphone size={48} opacity={0.3} />
                        </Box>
                      )}

                      {ann.is_pinned && (
                        <Chip
                          icon={<Pin size={12} color="#fff" />}
                          label="ปักหมุด"
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 10,
                            right: 10,
                            bgcolor: '#D97706',
                            color: '#FFFFFF',
                            fontWeight: 700,
                            fontSize: '0.72rem',
                          }}
                        />
                      )}
                    </Box>

                    {/* Card Body */}
                    <Box sx={{ p: 2.5, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: '#64748B', fontSize: '0.8rem' }}>
                        <Calendar size={13} />
                        <span>
                          {new Date(ann.created_at).toLocaleDateString('th-TH', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })}
                        </span>
                        <span>•</span>
                        <Eye size={13} />
                        <span>{ann.views_count || 0} views</span>
                      </Box>

                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: '#1E3A8A',
                          lineHeight: 1.35,
                          fontSize: '1.05rem',
                          mb: 1.2,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {ann.title}
                      </Typography>

                      <Box
                        sx={{
                          color: '#64748B',
                          fontSize: '0.88rem',
                          lineHeight: 1.6,
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          flexGrow: 1,
                          mb: 2,
                          '& p': { m: 0 },
                        }}
                        dangerouslySetInnerHTML={{
                          __html: (ann.content || '')
                            .replace(/src="\/uploads\//g, 'src="/api/v1/uploads/')
                            .replace(/href="\/uploads\//g, 'href="/api/v1/uploads/'),
                        }}
                      />

                      <Divider sx={{ my: 1.5 }} />

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                          โดย: {ann.author_name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 700, color: 'primary.main', display: 'flex', alignItems: 'center' }}
                        >
                          อ่านต่อ <ChevronRight size={14} />
                        </Typography>
                      </Box>
                    </Box>
                  </GlassCard>
                </Grid>
              ))}

              {filteredAnnouncements.length === 0 && !loading && (
                <Grid size={{ xs: 12 }}>
                  <GlassCard sx={{ p: 6, textAlign: 'center' }}>
                    <Typography variant="h6" color="text.secondary">
                      ไม่พบข่าวประชาสัมพันธ์ที่ตรงกับการค้นหา
                    </Typography>
                  </GlassCard>
                </Grid>
              )}
            </Grid>
          </Box>
        )}
      </Container>

      {/* Footer */}
      <Footer />
    </Box>
  );
};

export default PublicAnnouncements;
